import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { CONTAINS_LETTER } from '../../lib/validators'
import {
  Pencil,
  Plus,
  Trash2,
  ShieldCheck,
  Network,
  ChevronRight,
  X,
  Info,
  GripVertical,
} from 'lucide-react'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  pointerWithin,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { toast } from 'sonner'
import { Button } from '../../components/ui/Button'
import { TextField, TextAreaField, FieldLabel } from '../../components/ui/Field'
import { Modal } from '../../components/ui/Modal'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { Breadcrumb } from '../../components/Breadcrumb'
import { DataTable } from '../../components/table/DataTable'
import { RowActionsMenu } from '../../components/table/RowActionsMenu'
import { api, extractApiError } from '../../lib/api'
import { formatDateTime, formatNumber } from '../../lib/format'
import {
  seedBoard,
  applyDragOver,
  applyDragEnd,
  moveRoleTo,
  addLevel as addLevelToBoard,
  removeLevel as removeLevelFromBoard,
  savePayload as buildSavePayload,
} from '../../lib/work-role-hierarchy'
import {
  operationCatalogueResponse,
  workRoleAnalyticsResponse,
  workRoleList,
  workRoleRow,
} from '../../lib/types'


/** Form schema. The dialog asks for Name + Description + Operations
 *  (and Status in Edit mode).
 *
 *  A Work Role is defined entirely by the Operations the admin ticks —
 *  page access, task visibility, delegation, report access, and so on.
 *  There are no built-in roles or hierarchy levels: each organisation
 *  builds its own roles from scratch and a role can do exactly what its
 *  ticked operations allow (an empty set = the baseline self-only
 *  access). */
const workRoleSchema = z.object({
  name: z
    .string({ message: 'Work Role Name is required.' })
    .trim()
    .min(1, 'Work Role Name is required.')
    .min(2, 'Work Role Name must be at least 2 characters.')
    .max(120, 'Work Role Name must be at most 120 characters.')
    .regex(
      CONTAINS_LETTER,
      'Work Role Name must contain at least one letter.',
    ),
  description: z
    .string()
    .trim()
    .max(500, 'Description must be at most 500 characters.')
    .optional()
    .or(z.literal('')),
  status: z.enum(['active', 'archived']),
  operations: z.array(z.string()).default([]),
  // Seniority is no longer set here — it's arranged on the Work Roles
  // page via "Arrange Hierarchy" (drag to order), which writes the
  // rank. The dialog stays focused on what a role IS (name, description,
  // operations), not where it sits in the chain.
})

/** Access level — the primary control in the Add/Edit Work Role dialog.
 *  A role's VISIBILITY is one of three levels; picking one sets the
 *  underlying scope operations in a single choice (pages follow
 *  automatically via `withRequiredPages`). The granular per-operation
 *  checkboxes still exist under "Advanced" for the rare custom mix, and
 *  "Assign tasks" (Delegate) is an orthogonal capability toggle.
 *
 *  The level is DERIVED from the saved operations (they remain the backend
 *  source of truth), so the dialog stays in sync no matter how a role was
 *  built. Seniority between same-level roles is the rank in Arrange
 *  Hierarchy, not a separate permission. */
const DEPARTMENT_SCOPE_OPS = [
  'tasks.view_department',
  'reports.view_department',
  'members.view_department',
  'leaderboard.view_department',
]
const COMPANY_SCOPE_OPS = [
  'tasks.view_workspace',
  'reports.view_workspace',
  'members.view_workspace',
  'leaderboard.view_workspace',
]
const ACCESS_LEVELS = [
  {
    id: 'self',
    label: 'Self only',
    description: 'Sees only their own tasks and reports.',
    scopeOps: [],
  },
  {
    id: 'department',
    label: 'Department',
    description:
      "Sees their department's tasks, reports, members and leaderboard.",
    scopeOps: DEPARTMENT_SCOPE_OPS,
  },
  {
    id: 'company',
    label: 'Company-wide',
    description: 'Sees everything across the whole company.',
    scopeOps: COMPANY_SCOPE_OPS,
  },
]

/** Map a saved operations array back to an access level for the radio.
 *  Looks ONLY at the visibility-scope ops (pages + delegate are
 *  orthogonal). A clean match to one level wins; a partial or mixed set
 *  (e.g. department tasks but company reports) reads as 'custom', which
 *  surfaces the Advanced checkboxes. */
function deriveAccessLevel(ops) {
  const set = new Set(ops)
  const allCompany = COMPANY_SCOPE_OPS.every((k) => set.has(k))
  const allDept = DEPARTMENT_SCOPE_OPS.every((k) => set.has(k))
  const hasCompany = COMPANY_SCOPE_OPS.some((k) => set.has(k))
  const hasDept = DEPARTMENT_SCOPE_OPS.some((k) => set.has(k))
  // A complete company-scope set is the broadest level — classify as
  // company-wide even when department-scope ops are also present.
  if (allCompany) return 'company'
  // A complete department-scope set, with company scope NOT complete.
  if (allDept) return 'department'
  // No scope ops at all → the baseline self-only role.
  if (!hasCompany && !hasDept) return 'self'
  // Anything else is a partial or mixed set → custom.
  return 'custom'
}
const ACCESS_LABEL = {
  self: 'Self only',
  department: 'Department',
  company: 'Company-wide',
  custom: 'Custom',
}

/** A scope/action operation is useless without the page that surfaces its
 *  data — e.g. "View tasks across the department" needs the Team Tasks page
 *  to be reachable. This maps each such op to the page it requires, so the
 *  dialog keeps the two in sync: ticking a scope pulls its page in, and
 *  unticking a page drops the scopes that depend on it. Page access on its
 *  own stays valid (a self-only role still gets the Tasks page to see their
 *  own work) — only the reverse is enforced. */
const OP_REQUIRES_PAGE = {
  'tasks.view_department': 'pages.team_tasks',
  'tasks.view_workspace': 'pages.team_tasks',
  'tasks.delegate': 'pages.team_tasks',
  'reports.view_department': 'pages.team_reports',
  'reports.view_workspace': 'pages.team_reports',
  'members.view_department': 'pages.team_overview',
  'members.view_workspace': 'pages.team_overview',
  'leaderboard.view_department': 'pages.performance',
  'leaderboard.view_workspace': 'pages.performance',
}
/** Reverse index: page → the ops that require it. */
const PAGE_DEPENDENTS = Object.entries(OP_REQUIRES_PAGE).reduce(
  (acc, [op, page]) => {
    ;(acc[page] ??= []).push(op)
    return acc
  },
  {},
)
/** Ensure every dependent op in `ops` brings its required page along. */
function withRequiredPages(ops) {
  const set = new Set(ops)
  for (const op of ops) {
    const page = OP_REQUIRES_PAGE[op]
    if (page) set.add(page)
  }
  return [...set]
}

export function WorkRolesPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [hierarchyOpen, setHierarchyOpen] = useState(false)
  // "Advanced" (granular operation checkboxes) expander in the dialog.
  const [advancedOpen, setAdvancedOpen] = useState(false)
  // 'roles' = the management table; 'analytics' = the per-role dashboard.
  const [view, setView] = useState('roles')

  // Static operation catalogue — the canonical list of checkboxes the
  // admin can tick in the dialog. Cached forever; only changes on a
  // backend deploy. Grouped by module in `operationsByModule` below.
  const operationsCatalogueQuery = useQuery({
    queryKey: ['work-roles', 'operation-catalogue'],
    queryFn: async () => {
      const res = await api.get('/work-roles/operation-catalogue')
      return operationCatalogueResponse.parse(res.data)
    },
    staleTime: Infinity,
  })

  // Group the flat catalogue by module so the dialog reads as a tidy
  // matrix rather than one long list. Re-derives only when the cached
  // response changes (which is essentially never within a session).
  const operationsByModule = useMemo(() => {
    const catalogue = operationsCatalogueQuery.data?.data ?? []
    const groups = new Map()
    for (const row of catalogue) {
      if (!groups.has(row.module)) groups.set(row.module, [])
      groups.get(row.module).push(row)
    }
    return [...groups.entries()].map(([module, items]) => ({ module, items }))
  }, [operationsCatalogueQuery.data])

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['work-roles', 'list'],
    queryFn: async () => {
      const res = await api.get('/work-roles')
      return workRoleList.parse(res.data)
    },
  })

  // DataTable owns search / sort / pagination / columns / export now.
  // `allRows` still feeds the "Arrange Hierarchy" gate + modal and the
  // empty-state copy.
  const allRows = useMemo(() => data?.data ?? [], [data])

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Work Role Name',
        pinnable: true,
        minWidth: 240,
        sortAccessor: (r) => (r.name ?? '').toLowerCase(),
        searchAccessor: (r) => `${r.name ?? ''} ${r.description ?? ''}`,
        exportValue: (r) => r.name,
        render: (r) => (
          <div className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)]"
            >
              <ShieldCheck size={14} strokeWidth={1.6} />
            </span>
            <span className="text-[color:var(--color-ink-strong)] font-medium">
              {r.name}
            </span>
          </div>
        ),
      },
      {
        key: 'description',
        header: 'Description',
        grow: true,
        minWidth: 240,
        cellClassName: 'whitespace-normal break-words',
        sortAccessor: (r) => (r.description ?? '').toLowerCase(),
        exportValue: (r) => r.description ?? '',
        render: (r) =>
          r.description?.trim() || (
            <span className="text-[color:var(--color-ink-subtle)]">—</span>
          ),
      },
      {
        key: 'access',
        header: 'Access',
        minWidth: 150,
        sortAccessor: (r) =>
          ACCESS_LABEL[deriveAccessLevel(r.operations ?? [])] ?? '',
        exportValue: (r) => ACCESS_LABEL[deriveAccessLevel(r.operations ?? [])],
        render: (r) => {
          const lvl = deriveAccessLevel(r.operations ?? [])
          const canDelegate = (r.operations ?? []).includes('tasks.delegate')
          return (
            <span
              title={`${ACCESS_LABEL[lvl]} access${canDelegate ? ' · can assign tasks' : ''}`}
              className="inline-flex whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-dim)] border border-[color:var(--color-accent-border)]"
            >
              {ACCESS_LABEL[lvl]}
            </span>
          )
        },
      },
      {
        key: 'rank',
        header: 'Rank',
        minWidth: 80,
        sortAccessor: (r) =>
          typeof r.rank === 'number' ? r.rank : Number.MAX_SAFE_INTEGER,
        exportValue: (r) => (typeof r.rank === 'number' ? String(r.rank) : ''),
        render: (r) => (
          <span className="text-[color:var(--color-ink-strong)]">
            {typeof r.rank === 'number' ? r.rank : '—'}
          </span>
        ),
      },
      {
        key: 'users',
        header: 'Users',
        minWidth: 90,
        sortAccessor: (r) => Number(r.userCount ?? 0),
        exportValue: (r) => String(r.userCount ?? 0),
        render: (r) => (
          <span className="text-[color:var(--color-ink)]">{r.userCount}</span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: 120,
        sortAccessor: (r) => r.status ?? '',
        exportValue: (r) => (r.status === 'active' ? 'Active' : 'Inactive'),
        render: (r) => <WorkRoleStatusPill status={r.status} />,
      },
      {
        key: 'createdAt',
        header: 'Created On',
        minWidth: 160,
        sortAccessor: (r) => (r.createdAt ? new Date(r.createdAt) : null),
        exportValue: (r) => formatDateTime(r.createdAt),
        render: (r) => (
          <span className="whitespace-nowrap text-[color:var(--color-ink-muted)]">
            {formatDateTime(r.createdAt)}
          </span>
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        pinnedRight: true,
        align: 'center',
        minWidth: 72,
        sortable: false,
        exportable: false,
        hideable: false,
        render: (r) => (
          <RowActionsMenu
            label={`Actions for ${r.name}`}
            items={[
              { key: 'edit', label: 'Edit', icon: Pencil, onClick: () => openEdit(r) },
              {
                key: 'delete',
                label: 'Delete',
                icon: Trash2,
                danger: true,
                separatorBefore: true,
                // A role with members assigned can't be deleted — the
                // backend enforces the same 409; surface it up front.
                onClick: () => {
                  if (r.userCount > 0) {
                    toast.error(
                      `${r.userCount} member${r.userCount === 1 ? ' is' : 's are'} assigned to "${r.name}". Reassign ${r.userCount === 1 ? 'them' : 'them all'} to another Work Role before you can delete it.`,
                    )
                    return
                  }
                  setConfirmDelete(r)
                },
              },
            ]}
          />
        ),
      },
    ],
    // openEdit / setConfirmDelete close over stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const emptyState =
    allRows.length === 0
      ? {
          icon: ShieldCheck,
          title: 'No work roles yet',
          body: 'Create the roles your company needs and tick the operations each one is allowed to perform.',
        }
      : {
          icon: ShieldCheck,
          title: 'No work roles match your search',
          body: 'Try a different name or description, or clear the search.',
        }

  const form = useForm({
    resolver: zodResolver(workRoleSchema),
    defaultValues: {
      name: '',
      description: '',
      status: 'active',
      operations: [],
    },
  })
  const selectedOperations = form.watch('operations') ?? []
  // Derived dialog state: which access level the current operations map to,
  // and whether to reveal the Advanced checkboxes (always shown for a custom
  // mix so nothing is hidden).
  const accessLevel = deriveAccessLevel(selectedOperations)
  const showAdvanced = advancedOpen || accessLevel === 'custom'
  // The Access level above now fully drives the operation set (scope + its
  // required pages + delegate). The granular Advanced checkboxes are shown
  // READ-ONLY as a preview of what the chosen level grants — they can no longer
  // be toggled individually (removes confusing custom mixes, e.g. a junior role
  // with company-wide reports). Flip to `false` to restore the custom editor.
  const advancedReadOnly = true

  function toggleOperation(key) {
    const current = form.getValues('operations') ?? []
    let next
    if (current.includes(key)) {
      next = current.filter((k) => k !== key)
      // Unticking a page drops the scope ops that need it — they'd be inert
      // without the page to surface their data.
      const dependents = PAGE_DEPENDENTS[key]
      if (dependents) next = next.filter((k) => !dependents.includes(k))
    } else {
      // Ticking a scope/action op pulls in its host page so the role can
      // actually reach the data.
      next = withRequiredPages([...current, key])
    }
    form.setValue('operations', next, { shouldDirty: true })
  }

  function toggleAllInModule(items, allOn) {
    const keys = items.map((i) => i.key)
    const current = form.getValues('operations') ?? []
    let next = allOn
      ? current.filter((k) => !keys.includes(k))
      : [...new Set([...current, ...keys])]
    if (allOn) {
      // Deselecting a module that contains pages also drops those pages'
      // dependent scope ops, keeping the set consistent.
      for (const k of keys) {
        const dependents = PAGE_DEPENDENTS[k]
        if (dependents) next = next.filter((d) => !dependents.includes(d))
      }
    } else {
      next = withRequiredPages(next)
    }
    form.setValue('operations', next, { shouldDirty: true })
  }

  // Access-level radio handler. Sets the visibility-scope ops for the level
  // (pages follow via withRequiredPages). Delegation is coupled to the scope:
  // Department / Company-wide roles can assign tasks to others, while a
  // Self-only role cannot (it can't see other members' work, so there's no
  // one to delegate to). 'self' is therefore an empty set — the baseline
  // self-only role.
  function setAccessLevel(levelId) {
    const level = ACCESS_LEVELS.find((l) => l.id === levelId)
    const includeDelegate = levelId !== 'self'
    const next = withRequiredPages([
      ...level.scopeOps,
      ...(includeDelegate ? ['tasks.delegate'] : []),
    ])
    form.setValue('operations', next, {
      shouldDirty: true,
      shouldValidate: true,
    })
  }


  function openCreate() {
    setEditing(null)
    setAdvancedOpen(false)
    form.reset({
      name: '',
      description: '',
      status: 'active',
      operations: [],
    })
    setModalOpen(true)
  }

  function openEdit(row) {
    const parsed = workRoleRow.parse(row)
    setEditing(parsed)
    setAdvancedOpen(false)
    form.reset({
      name: parsed.name,
      description: parsed.description ?? '',
      status: parsed.status,
      operations: parsed.operations ?? [],
    })
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setEditing(null)
    form.reset()
  }

  const saveMutation = useMutation({
    mutationFn: async (values) => {
      // A role is its name, description, and the operations the admin
      // ticked. Seniority (rank) is set separately via "Arrange Hierarchy",
      // so it's deliberately omitted here — on edit the backend leaves the
      // existing rank untouched; a new role starts unranked until added to
      // the hierarchy.
      const payload = {
        // Always send the (trimmed) description, empty string included, so
        // clearing it on edit reaches the backend and resets it to null.
        // `|| undefined` dropped the key, which the update service reads as
        // "leave unchanged" — so a cleared description silently persisted.
        name: values.name.trim(),
        description: values.description?.trim() ?? '',
        operations: values.operations ?? [],
      }
      if (editing) {
        payload.status = values.status
        const res = await api.patch(`/work-roles/${editing.id}`, payload)
        return { mode: 'edit', row: res.data }
      }
      const res = await api.post('/work-roles', payload)
      return { mode: 'create', row: res.data }
    },
    onSuccess: (result) => {
      closeModal()
      void qc.invalidateQueries({ queryKey: ['work-roles'] })
      if (result.mode === 'edit') {
        toast.success('Work Role updated successfully.')
        return
      }
      // A new role lands unranked; the admin can place it later via the
      // "Arrange Hierarchy" button on the page (no toast action needed).
      toast.success('Work Role added successfully.')
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      let mapped = false
      if (fields) {
        for (const [name, msg] of Object.entries(fields)) {
          if (name in form.getValues()) {
            form.setError(name, { message: msg })
            mapped = true
          }
        }
      }
      if (!mapped) toast.error(message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/work-roles/${id}`)
    },
    onSuccess: () => {
      toast.success('Work Role deleted successfully.')
      setConfirmDelete(null)
      void qc.invalidateQueries({ queryKey: ['work-roles'] })
    },
    onError: (err, id) => {
      const message = extractApiError(err).message
      // A blocked delete — the role still has members assigned (surfaced as
      // a 409 Conflict) — is recoverable: give the admin a one-click path to
      // the Members page filtered to this role so they can reassign people.
      if (err?.response?.status === 409) {
        toast.error(message, {
          action: {
            label: 'View members',
            onClick: () => {
              setConfirmDelete(null)
              navigate(`/members?workRoleId=${id}`)
            },
          },
        })
        return
      }
      toast.error(message)
    },
  })

  // "Arrange Hierarchy" save — sends the levels (most senior first), each an
  // array of Work Role ids that share that hierarchy level. The backend
  // assigns every role in a level the same rank, so multiple user-defined
  // roles can be peers at one level. Backend stays the single source of
  // truth for the rank numbers; the UI just supplies the grouping/order.
  const reorderMutation = useMutation({
    mutationFn: async (levels) => {
      await api.patch('/work-roles/reorder', { levels })
    },
    onSuccess: () => {
      toast.success('Hierarchy updated successfully.')
      setHierarchyOpen(false)
      void qc.invalidateQueries({ queryKey: ['work-roles'] })
    },
    onError: (err) => {
      toast.error(extractApiError(err).message)
    },
  })

  return (
    <div>
      <Breadcrumb current="Work Role Management" />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 mb-3">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)]">
            Work Role Management
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Create the roles your company needs and tick the
            operations each one is allowed to perform. A role can do
            exactly what its selected operations permit.
          </p>
        </div>
        {/* Right-aligned primary action — same `Button` + leading
         *  `Plus` icon shape used by Companies → "Create Company",
         *  Plans → "Create Plan", Departments → "Add Department", so
         *  the admin chrome reads with one consistent action grammar.
         *  Opens the same dialog the row pencil icon opens (it just
         *  starts blank and runs the POST branch of `saveMutation`). */}
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {/* Opens the org-chart-style arrange view, which sets each role's
           *  rank (seniority). Rank drives the visibility cascade on
           *  Reports, Performance, search and the member directory — a more
           *  senior role's data stays hidden from juniors. Hidden until
           *  there are at least two roles: a hierarchy of one is
           *  meaningless, so showing a greyed-out button would just be
           *  noise. It appears automatically once a second role exists. */}
          {(allRows?.length ?? 0) >= 2 ? (
            <Button
              variant="secondary"
              onClick={() => setHierarchyOpen(true)}
            >
              <Network size={14} />
              Arrange Hierarchy
            </Button>
          ) : null}
          <Button onClick={openCreate}>
            <Plus size={14} />
            Add Work Role
          </Button>
        </div>
      </div>

      {/* Roles ↔ Analytics tab toggle. Analytics is a per-role dashboard
       *  whose figures all aggregate live from Members / Tasks / Points. */}
      <div className="mb-3 inline-flex rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-0.5">
        {[
          { id: 'roles', label: 'Roles' },
          { id: 'analytics', label: 'Analytics' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setView(t.id)}
            className={
              'rounded px-3 py-1 text-sm cursor-pointer transition-colors ' +
              (view === t.id
                ? 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-strong)] font-medium'
                : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)]')
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {view === 'analytics' ? (
        <WorkRoleAnalytics
          onDrill={(id) => navigate(`/members?workRoleId=${id}`)}
        />
      ) : (
        <>
      <DataTable
        tableId="app-work-roles"
        columns={columns}
        rows={allRows}
        getRowId={(r) => r.id}
        loading={isLoading}
        error={isError ? extractApiError(error).message : null}
        title="Work Roles"
        exportBaseName="work-roles"
        exportTitle="Work Roles"
        searchPlaceholder="Search by Name or Description"
        selectable
        emptyState={emptyState}
      />
        </>
      )}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editing ? 'Edit Work Role' : 'Add Work Role'}
        // `lg` (max-w-2xl ≈ 672px) — wider than the original `ml`
        // (max-w-xl ≈ 576px) so the Operations matrix has breathing
        // room, but narrower than `xl` (max-w-4xl) which made the
        // dialog feel oversized for its content.
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={closeModal}
              disabled={saveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              form="work-role-form"
              type="submit"
              loading={saveMutation.isPending}
            >
              {editing ? 'Save Changes' : 'Add'}
            </Button>
          </>
        }
      >
        <form
          id="work-role-form"
          onSubmit={form.handleSubmit((v) => saveMutation.mutate(v))}
          noValidate
          className="space-y-4"
        >
          <TextField
            label="Work Role Name"
            required
            placeholder="Enter a Work Role Name"
            minLength={2}
            maxLength={120}
            error={form.formState.errors.name?.message}
            {...form.register('name')}
          />
          {/* Shared TextAreaField — the same primitive the Workflows dialog
           *  uses for this field — instead of a hand-rolled <textarea>. It
           *  ships `resize-y`, so the box can no longer be dragged sideways
           *  out of the dialog (a bare <textarea> defaults to `resize: both`),
           *  and `showCount` surfaces the 500-char cap that was already
           *  enforced but invisible. Description stays optional, so there's
           *  deliberately no minLength. */}
          <TextAreaField
            label="Description"
            placeholder="Enter a Description"
            maxLength={500}
            rows={3}
            showCount
            error={form.formState.errors.description?.message}
            {...form.register('description')}
          />

          {/* Access level — the primary visibility control. Picking one
           *  sets the role's scope operations in a single choice (the pages
           *  they need follow automatically). The granular checkboxes live
           *  under "Advanced". 'Custom' shows when a saved role's operations
           *  don't map cleanly onto one level. */}
          <div>
            <FieldLabel label="Access Level" />
            <div className="flex flex-col gap-2">
              {ACCESS_LEVELS.map((lvl) => {
                const active = accessLevel === lvl.id
                return (
                  <button
                    key={lvl.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setAccessLevel(lvl.id)}
                    className={
                      'flex items-start gap-2.5 rounded-md border px-3 py-2 text-left cursor-pointer transition-colors ' +
                      (active
                        ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent-bg)]'
                        : 'border-[color:var(--color-rule)] bg-[color:var(--color-card)] hover:border-[color:var(--color-accent-border)]')
                    }
                  >
                    <span
                      className={
                        'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ' +
                        (active
                          ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent)]'
                          : 'border-[color:var(--color-rule)]')
                      }
                    >
                      {active ? (
                        <span className="h-1.5 w-1.5 rounded-full bg-white" />
                      ) : null}
                    </span>
                    <span>
                      <span
                        className={
                          'block text-sm font-medium ' +
                          (active
                            ? 'text-[color:var(--color-accent-strong)]'
                            : 'text-[color:var(--color-ink-strong)]')
                        }
                      >
                        {lvl.label}
                      </span>
                      <span className="block text-xs text-[color:var(--color-ink-muted)]">
                        {lvl.description}
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
            {accessLevel === 'custom' ? (
              <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                Custom mix of operations — adjust them under Advanced below.
              </p>
            ) : null}
          </div>

          {/* Advanced — the granular operation checkboxes, collapsed by
           *  default and auto-revealed for a custom mix so nothing hides. */}
          <div>
            <button
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              className="flex items-center gap-1 text-sm text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] cursor-pointer"
            >
              <ChevronRight
                size={16}
                className={
                  'transition-transform ' + (showAdvanced ? 'rotate-90' : '')
                }
              />
              Advanced — operations granted by this access level
            </button>
            {showAdvanced ? (
              <div className="mt-2">
            {/* No inner scroll — the operations list expands to full
             *  height so the dialog body is the only scroll container
             *  (one scrollbar, not two). */}
            <div
              id="work-role-operations"
              role="group"
              aria-label="Operations"
              className="rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)]"
            >
              {operationsCatalogueQuery.isLoading ? (
                <div className="px-3 py-3 text-sm text-[color:var(--color-ink-muted)]">
                  Loading operations…
                </div>
              ) : operationsByModule.length === 0 ? (
                <div className="px-3 py-3 text-sm text-[color:var(--color-ink-muted)]">
                  No operations available.
                </div>
              ) : (
                operationsByModule.map(({ module, items }, gi) => {
                  const allOn = items.every((i) =>
                    selectedOperations.includes(i.key),
                  )
                  const someOn =
                    !allOn &&
                    items.some((i) => selectedOperations.includes(i.key))
                  // Delegation is owned by the access scope (Self-only roles
                  // can't delegate), so lock the Task Actions module — its only
                  // op is tasks.delegate — when the level is Self.
                  const moduleLocked =
                    accessLevel === 'self' &&
                    items.every((i) => i.key === 'tasks.delegate')
                  return (
                    <div
                      key={module}
                      className={
                        gi > 0
                          ? 'border-t border-[color:var(--color-rule)]'
                          : ''
                      }
                    >
                      <div className="flex items-center justify-between px-3 py-2 bg-[color:var(--color-canvas)]">
                        <span className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
                          {module}
                        </span>
                        <label
                          className={
                            'flex items-center gap-2 text-xs text-[color:var(--color-ink-muted)] ' +
                            (moduleLocked
                              ? 'opacity-50 cursor-not-allowed'
                              : advancedReadOnly
                                ? 'cursor-default'
                                : 'cursor-pointer')
                          }
                        >
                          <input
                            type="checkbox"
                            checked={allOn}
                            disabled={moduleLocked || advancedReadOnly}
                            ref={(el) => {
                              if (el) el.indeterminate = someOn
                            }}
                            onChange={() => toggleAllInModule(items, allOn)}
                            className="h-3.5 w-3.5 accent-[color:var(--color-accent)] disabled:cursor-not-allowed cursor-pointer"
                          />
                          Select all
                        </label>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2">
                        {items.map((op) => {
                          const checked = selectedOperations.includes(op.key)
                          // Self-only roles can't be granted delegation.
                          const opLocked =
                            accessLevel === 'self' &&
                            op.key === 'tasks.delegate'
                          return (
                            <label
                              key={op.key}
                              className={
                                'flex items-center gap-2 px-3 py-2 ' +
                                (opLocked
                                  ? 'opacity-50 cursor-not-allowed'
                                  : advancedReadOnly
                                    ? 'cursor-default'
                                    : 'cursor-pointer hover:bg-[color:var(--color-canvas)]')
                              }
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={opLocked || advancedReadOnly}
                                onChange={() => toggleOperation(op.key)}
                                className="h-4 w-4 accent-[color:var(--color-accent)] disabled:cursor-not-allowed cursor-pointer"
                              />
                              <span className="text-sm text-[color:var(--color-ink)]">
                                {op.label}
                              </span>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              {selectedOperations.length === 0
                ? 'This role grants the baseline self-only access. Choose an access level above to grant more.'
                : `${selectedOperations.length} operation${selectedOperations.length === 1 ? '' : 's'} granted by this access level. Change the access level above to adjust.`}
            </p>
              </div>
            ) : null}
          </div>

          {/* Status — edit-only. New roles default to 'active'. Admins
           *  can archive any Work Role; archived (Inactive) rows stop
           *  appearing in the Members page Work Role dropdown but the
           *  row remains for reference. */}
          {editing ? (
            <div>
              <FieldLabel label="Status" htmlFor="work-role-status" />
              <select
                id="work-role-status"
                {...form.register('status')}
                className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
              >
                <option value="active">Active</option>
                {/* DB value stays `archived` so the API contract is
                 *  unchanged; only the user-facing label flipped to
                 *  "Inactive" for consistency with the Workspaces +
                 *  Departments pages. */}
                <option value="archived">Inactive</option>
              </select>
            </div>
          ) : null}
        </form>
      </Modal>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete Work Role"
        message={
          confirmDelete
            ? `Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        confirming={deleteMutation.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) deleteMutation.mutate(confirmDelete.id)
        }}
      />

      <HierarchyModal
        key={hierarchyOpen ? 'hierarchy-open' : 'hierarchy-closed'}
        open={hierarchyOpen}
        roles={allRows}
        saving={reorderMutation.isPending}
        onClose={() => setHierarchyOpen(false)}
        onSave={(levels) => reorderMutation.mutate(levels)}
      />
    </div>
  )
}

/** Work Role status pill — Active (green) / Inactive (red). The DB
 *  `archived` status surfaces as "Inactive". Shared visual vocabulary
 *  with the Workspaces / Departments status columns. */
function WorkRoleStatusPill({ status }) {
  return (
    <span
      className={
        'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold text-white border border-transparent dark:border-white/10 ' +
        (status === 'active'
          ? 'bg-[color:var(--color-success)] dark:bg-white/[0.06] dark:text-emerald-300'
          : 'bg-[color:var(--color-priority-urgent)] dark:bg-white/[0.06] dark:text-rose-300')
      }
    >
      {status === 'active' ? 'Active' : 'Inactive'}
    </span>
  )
}

function Th({ children, className = '' }) {
  return (
    <th
      className={`px-4 py-3 font-medium text-sm ${className}`}
    >
      {children}
    </th>
  )
}

function Td({ children, className = '', colSpan, label }) {
  return (
    <td
      colSpan={colSpan}
      data-label={label}
      className={`px-4 py-3 align-middle ${className}`}
    >
      {children}
    </td>
  )
}

/** Work Role analytics dashboard — a KPI strip + a per-role table whose
 *  every figure is a live aggregate (members, task load, completion,
 *  points) keyed by the role members carry. Clicking a role drills through
 *  to the Members list filtered to that role. */
function WorkRoleAnalytics({ onDrill }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['work-roles', 'analytics'],
    queryFn: async () => {
      const res = await api.get('/work-roles/analytics')
      return workRoleAnalyticsResponse.parse(res.data)
    },
  })

  if (isLoading) {
    return (
      <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-8 text-center text-sm text-[color:var(--color-ink-muted)]">
        Loading analytics…
      </div>
    )
  }
  if (isError) {
    return (
      <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-8 text-center text-sm text-[color:var(--color-priority-urgent)]">
        {extractApiError(error).message}
      </div>
    )
  }

  const { data: rows, totals } = data
  const kpis = [
    {
      label: 'Work Roles',
      value: totals.roleCount,
      // The "N members unassigned" hint was dropped (2026-07-17) alongside
      // the synthetic "Unassigned" table row — this tab now reports only on
      // the roles the Company Admin created.
      hint: 'in this company',
    },
    {
      label: 'Members assigned',
      value: totals.membersAssigned,
      hint: 'across active roles',
    },
    {
      label: 'Task completion',
      value: `${totals.completionRate}%`,
      hint: 'company-wide',
    },
  ]

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {kpis.map((k) => (
          <div
            key={k.label}
            className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4"
          >
            <div className="text-sm text-[color:var(--color-ink-muted)]">
              {k.label}
            </div>
            <div className="mt-1 text-2xl font-semibold text-[color:var(--color-ink-strong)]">
              {k.value}
            </div>
            <div className="text-xs text-[color:var(--color-ink-subtle)]">
              {k.hint}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)]">
              <tr>
                <Th>Work Role</Th>
                <Th className="text-right">Members</Th>
                <Th className="text-right">Open</Th>
                <Th className="text-right">Overdue</Th>
                <Th className="text-right">Done</Th>
                <Th className="text-right">Completion</Th>
                <Th className="text-right">Points</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <Td
                    colSpan={7}
                    className="text-center text-[color:var(--color-ink-muted)] py-8"
                  >
                    No work roles yet.
                  </Td>
                </tr>
              ) : (
                rows.map((r) => {
                  const clickable = Boolean(r.id) && r.memberCount > 0
                  return (
                    <tr
                      key={r.id || 'unassigned'}
                      onClick={clickable ? () => onDrill(r.id) : undefined}
                      className={
                        'border-t border-[color:var(--color-rule)] ' +
                        (clickable
                          ? 'hover:bg-[color:var(--color-canvas)] cursor-pointer'
                          : '')
                      }
                    >
                      <Td>
                        <div className="flex items-center gap-2">
                          <span
                            className={
                              r.id
                                ? 'font-medium text-[color:var(--color-ink-strong)]'
                                : 'text-[color:var(--color-ink-muted)]'
                            }
                          >
                            {r.name}
                          </span>
                          {r.status === 'archived' ? (
                            <span className="rounded-full border border-[color:var(--color-rule)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--color-ink-muted)]">
                              Inactive
                            </span>
                          ) : null}
                        </div>
                      </Td>
                      <Td className="text-right">{r.memberCount}</Td>
                      <Td className="text-right">{r.openTasks}</Td>
                      <Td
                        className={
                          'text-right ' +
                          (r.overdueTasks > 0
                            ? 'text-[color:var(--color-priority-urgent)]'
                            : '')
                        }
                      >
                        {r.overdueTasks}
                      </Td>
                      <Td className="text-right">{r.completedTasks}</Td>
                      <Td className="text-right">{r.completionRate}%</Td>
                      <Td className="text-right">
                        {formatNumber(r.points.total)}
                      </Td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-[color:var(--color-ink-muted)]">
        Figures aggregate live from Members, Tasks and Points — click a role
        to open its members. (Report compliance is coming next.)
      </p>
    </div>
  )
}

// seedLevels / byNameCmp / the board transforms live in
// lib/work-role-hierarchy.js so they can be unit-tested without a DOM.

// Per-level accent palette for the org-chart (decorative). Saturated colours
// read on both light and dark card backgrounds (white text on the badge; the
// pill/border use an alpha tint that adapts to whatever sits behind it).
const LEVEL_PALETTE = [
  '#6366f1', // indigo
  '#14b8a6', // teal
  '#a855f7', // purple
  '#f59e0b', // amber
  '#ec4899', // pink
  '#0ea5e9', // sky
]
const levelColorAt = (i) => (i >= 0 ? LEVEL_PALETTE[i % LEVEL_PALETTE.length] : null)
function hexToRgba(hex, a) {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
}

/** Level-grouped hierarchy view. Work Roles are grouped into hierarchy LEVELS
 *  (Level 1 = most senior). MULTIPLE user-defined roles can share a level —
 *  they become PEERS at the same rank (neither is senior to the other). Roles
 *  with no rank sit in a "Not in the hierarchy" tray. A role can be dragged
 *  between levels / reordered, or moved with its level selector. Save sends the
 *  levels (most senior first) to the bulk re-rank endpoint, which assigns every
 *  role in a level the same rank and unranks anything left out. Working copies
 *  are local and only committed on Save — the parent flips `key` on open so each
 *  open remounts with a fresh seed. Hierarchy is by LEVEL/RANK only, never by
 *  role name. */
function HierarchyModal({ open, roles, saving, onClose, onSave }) {
  // One board object so drag moves between containers update atomically.
  const [board, setBoard] = useState(() => seedBoard(roles))
  const [activeId, setActiveId] = useState(null)
  // Monotonic id source for brand-new levels — stable across re-index.
  const nextLevelId = useRef(0)
  // Set true when onDragOver reparents a role into a different level.
  // Suppresses the within-container sort on dragEnd (dnd-kit multiple-
  // containers pattern) so a cross-level drop doesn't get undone.
  const recentlyMovedToNewContainer = useRef(false)

  // Drag by a handle, so the card's select stays clickable. A tiny activation
  // distance keeps clicks from registering as drags.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Container/board helpers + the drag transforms are pure functions in
  // lib/work-role-hierarchy.js (imported above) so they can be unit-tested.

  // ── drag handlers (multi-container sortable) ──
  //
  // Cross-container moves preview live on dragOver; within-container
  // reorder commits on dragEnd. `pointerWithin` collision detection
  // (instead of closestCorners) stops the source/target bounce that
  // caused React error #185 with rect-based algorithms.
  function handleDragStart(e) {
    recentlyMovedToNewContainer.current = false
    setActiveId(e.active.id)
  }
  function handleDragOver(e) {
    const { active, over } = e
    if (!over || active.id === over.id) return
    setBoard((prev) => {
      const { board: next, reparented } = applyDragOver(prev, active.id, over.id)
      if (reparented) recentlyMovedToNewContainer.current = true
      return next
    })
  }
  function handleDragEnd(e) {
    const { active, over } = e
    setActiveId(null)
    if (!over) return
    if (recentlyMovedToNewContainer.current) {
      recentlyMovedToNewContainer.current = false
      return
    }
    setBoard((prev) => applyDragEnd(prev, active.id, over.id))
  }

  // Belt-and-braces reset for the cross-container flag after layout
  // settles — mirrors the dnd-kit Multiple Containers example.
  useEffect(() => {
    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false
    })
  }, [board])

  // ── selector / button moves (accessible fallback for drag) ──
  function moveRole(role, dest) {
    setBoard((prev) => moveRoleTo(prev, role, dest))
  }
  function addLevel() {
    setBoard((prev) => addLevelToBoard(prev, `new-${nextLevelId.current++}`))
  }
  function removeLevel(levelId) {
    setBoard((prev) => removeLevelFromBoard(prev, levelId))
  }
  const savePayload = () => buildSavePayload(board)

  const { levels, unranked } = board
  const totalLevels = levels.length
  const activeLevelIndex =
    activeId != null
      ? levels.findIndex((l) => l.roles.some((r) => r.id === activeId))
      : -1
  const activeRole =
    activeId != null
      ? [...levels.flatMap((l) => l.roles), ...unranked].find(
          (r) => r.id === activeId,
        ) ?? null
      : null

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Arrange Hierarchy"
      size="xl"
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <Button variant="secondary" onClick={addLevel} disabled={saving}>
            <Plus size={15} />
            Add New Level
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => onSave(savePayload())} loading={saving}>
              Save Hierarchy
            </Button>
          </div>
        </div>
      }
    >
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={() => {
          recentlyMovedToNewContainer.current = false
          setActiveId(null)
        }}
      >
        <div className="space-y-4">
          <p className="text-sm text-[color:var(--color-ink-muted)]">
            Group Work Roles into levels — Level 1 is most senior, and roles in
            the same level are peers. Drag a card to move it between levels or
            reorder it, or use its level selector.
          </p>

          {totalLevels === 0 ? (
            <div className="rounded-lg border border-dashed border-[color:var(--color-rule)] p-6 text-center text-sm text-[color:var(--color-ink-muted)]">
              No levels yet. Use “Add New Level”, then drag roles into it from
              the tray below.
            </div>
          ) : (
            // Compact lanes: a slim numbered rail on the left (levels joined by
            // a vertical flow line) and, on the right, each level's Work Role
            // cards that WRAP — so every peer stays visible, however many share
            // the level, with no sideways scrolling.
            <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-3">
              <div className="grid grid-cols-[36px_minmax(0,1fr)] gap-x-2">
                {levels.map((level, i) => (
                  <Fragment key={level.id}>
                    {/* Flow line linking this level's badge to the one above
                        (left rail only — cards wrap, so no per-card stub). */}
                    {i > 0 ? (
                      <>
                        <div className="flex justify-center">
                          <div className="h-5 w-[2px] bg-[color:var(--color-ink-subtle)]" />
                        </div>
                        <div aria-hidden="true" />
                      </>
                    ) : null}

                    {/* Slim level badge. */}
                    <div
                      className="flex flex-col items-center gap-1 pt-1"
                      title={`Level ${i + 1} — ${
                        i === 0
                          ? 'most senior'
                          : i === totalLevels - 1
                            ? 'most junior'
                            : 'peers'
                      }`}
                    >
                      <span
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold text-white shadow-sm"
                        style={{ backgroundColor: levelColorAt(i) }}
                      >
                        {i + 1}
                      </span>
                      <span className="text-center text-[9px] font-semibold uppercase leading-tight tracking-wide text-[color:var(--color-ink-subtle)]">
                        {i === 0
                          ? 'Senior'
                          : i === totalLevels - 1
                            ? 'Junior'
                            : 'Peers'}
                      </span>
                    </div>

                    {/* Role cards — wrap to keep every peer visible. */}
                    <div className="min-w-0 pb-1 pt-0.5">
                      <LevelDropZone
                        containerId={level.id}
                        roles={level.roles}
                        levelIndex={i}
                        levels={levels}
                        color={levelColorAt(i)}
                        onMove={moveRole}
                        onRemoveEmpty={() => removeLevel(level.id)}
                        wrap
                      />
                    </div>
                  </Fragment>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-start gap-2 rounded-lg border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] px-3 py-2 text-xs text-[color:var(--color-ink)]">
            <Info
              size={14}
              className="mt-0.5 shrink-0 text-[color:var(--color-accent-strong)]"
            />
            <span>
              <span className="font-semibold">Tip:</span> drag and drop cards to
              move roles between levels or reorder within a level. Roles sharing
              a level are peers — neither is senior to the other.
            </span>
          </div>

          {unranked.length > 0 ? (
            <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-3">
              <p className="mb-1 text-sm font-medium text-[color:var(--color-ink-strong)]">
                Not in the hierarchy
              </p>
              <p className="mb-2 text-xs text-[color:var(--color-ink-muted)]">
                These roles opt out of the visibility cascade. Drag one into a
                level to place it in the hierarchy.
              </p>
              <LevelDropZone
                containerId="unranked"
                roles={unranked}
                levelIndex={-1}
                levels={levels}
                color={null}
                onMove={moveRole}
                wrap
              />
            </div>
          ) : null}
        </div>

        <DragOverlay dropAnimation={null}>
          {activeRole ? (
            <RoleCardVisual
              role={activeRole}
              levelIndex={activeLevelIndex}
              levels={levels}
              color={levelColorAt(activeLevelIndex)}
              dragging
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </Modal>
  )
}

/** A droppable, sortable container of role cards — one hierarchy level (or the
 *  "Not in hierarchy" tray when `containerId === 'unranked'`). Empty levels
 *  stay droppable so a card can be dragged in. */
function LevelDropZone({
  containerId,
  roles,
  levelIndex,
  levels,
  color,
  onMove,
  onRemoveEmpty,
}) {
  const { setNodeRef, isOver } = useDroppable({ id: containerId })
  const empty = roles.length === 0
  // Keep one stable droppable wrapper for empty AND filled levels.
  // Switching between two different ref targets mid-drag remounted
  // the droppable and confused dnd-kit's collision detection.
  return (
    <div
      ref={setNodeRef}
      className={
        'flex flex-wrap justify-center gap-3 rounded-xl ' +
        (empty ? 'min-h-[52px] ' : '') +
        (isOver
          ? 'ring-2 ring-[color:var(--color-accent)] ring-offset-2 ring-offset-[color:var(--color-canvas)]'
          : '')
      }
    >
      {empty ? (
        <div
          className={
            'flex items-center gap-2 rounded-xl border border-dashed px-4 py-3 text-xs text-[color:var(--color-ink-muted)] ' +
            (isOver
              ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent-bg)]'
              : 'border-[color:var(--color-rule)]')
          }
        >
          Drag a role here
          {onRemoveEmpty ? (
            <button
              type="button"
              onClick={onRemoveEmpty}
              aria-label="Remove empty level"
              className="rounded p-0.5 text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] cursor-pointer"
            >
              <X size={13} />
            </button>
          ) : null}
        </div>
      ) : (
        <SortableContext
          items={roles.map((r) => r.id)}
          strategy={horizontalListSortingStrategy}
        >
          {roles.map((role) => (
            <SortableRoleCard
              key={role.id}
              role={role}
              levelIndex={levelIndex}
              levels={levels}
              color={color}
              onMove={onMove}
            />
          ))}
        </SortableContext>
      )}
    </div>
  )
}

/** A single draggable role card (sortable within / across levels). */
function SortableRoleCard({ role, levelIndex, levels, color, onMove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: role.id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Collapse the in-list placeholder while the DragOverlay carries
    // the visual — a semi-transparent source card still participated
    // in collision detection and contributed to container bounce.
    opacity: isDragging ? 0 : 1,
  }
  return (
    <div ref={setNodeRef} style={style}>
      <RoleCardVisual
        role={role}
        levelIndex={levelIndex}
        levels={levels}
        color={color}
        onMove={onMove}
        handleProps={{ ...attributes, ...listeners }}
      />
    </div>
  )
}

/** Presentational Work Role card — initial badge, name, "Level n" pill,
 *  operations count, a level selector, and a drag handle. Per-level colour is
 *  decorative; hierarchy is by rank, never by role name. Reused by the drag
 *  overlay (no handle / no selector). */
function RoleCardVisual({
  role,
  levelIndex,
  levels,
  color,
  onMove,
  handleProps,
  dragging = false,
}) {
  const opCount = role.operations?.length ?? 0
  const archived = role.status === 'archived'
  const initial = (role.name?.trim()?.[0] ?? '?').toUpperCase()
  const currentValue = levelIndex >= 0 ? levels[levelIndex]?.id : 'unranked'
  return (
    <div
      className={
        'flex w-60 shrink-0 items-start gap-2.5 rounded-xl border bg-[color:var(--color-card)] p-2.5 ' +
        (dragging ? 'shadow-lg' : 'shadow-sm')
      }
      style={{ borderColor: color ? hexToRgba(color, 0.5) : 'var(--color-rule)' }}
    >
      <span
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white"
        style={{ backgroundColor: color ?? 'var(--color-ink-muted)' }}
      >
        {initial}
      </span>
      <div className="min-w-0 flex-1">
        {/* The role name gets the full width — the level is already shown by the
            lane badge on the left, so no per-card "Level n" pill is needed. */}
        <span
          title={role.name}
          className={
            'block break-words text-sm font-semibold ' +
            (archived
              ? 'text-[color:var(--color-ink-muted)]'
              : 'text-[color:var(--color-ink-strong)]')
          }
        >
          {role.name}
        </span>
        <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-[color:var(--color-ink-muted)]">
          <span>
            {opCount} operation{opCount === 1 ? '' : 's'}
          </span>
          {archived ? (
            <span className="rounded-full border border-[color:var(--color-rule)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide">
              Inactive
            </span>
          ) : null}
        </div>
        {onMove ? (
          <select
            value={currentValue}
            onChange={(e) => onMove(role, e.target.value)}
            aria-label={`Hierarchy level for ${role.name}`}
            className="mt-1.5 w-full rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-2 py-1 text-xs text-[color:var(--color-ink)] cursor-pointer focus:outline-none focus:border-[color:var(--color-accent)]"
          >
            {levels.map((level, i) => (
              <option key={level.id} value={level.id}>
                Level {i + 1}
              </option>
            ))}
            <option value="unranked">Not in hierarchy</option>
          </select>
        ) : null}
      </div>
      {handleProps ? (
        <button
          type="button"
          {...handleProps}
          aria-label={`Drag ${role.name}`}
          className="mt-0.5 shrink-0 cursor-grab touch-none rounded p-0.5 text-[color:var(--color-ink-subtle)] hover:text-[color:var(--color-ink)]"
        >
          <GripVertical size={16} />
        </button>
      ) : null}
    </div>
  )
}

